import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import {
  ActivityIndicator,
  Alert,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import FallbackImage from '../components/common/FallbackImage';
import styled from 'styled-components/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useAuth } from '../context/AuthContext';
import { useNegotiationRealtime } from '../hooks/useNegotiationRealtime';
import { useNegotiationEvents } from '../hooks/useNegotiationEvents';
import { useNavLinks } from '../hooks/useNavLinks';
import {
  selectActiveDrafts,
  selectActionSurface,
  buildBannerStack,
} from '../utils/negotiation/detailSelectors';
import { REDESIGN_COLORS } from '../styles/colors';
import {
  NEGOTIATION_CONFIG,
  FORM_BRIDGE_DISMISSAL_KEY_PREFIX,
  STATUS_LABELS,
} from '../config/negotiationConfig';
import { apiService } from '../services/apiService';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';

// Detail-redesign components (Tasks 14-26)
import DraftBlock from '../components/negotiation/detail/DraftBlock';
import MobileDraftSheet from '../components/negotiation/detail/MobileDraftSheet';
import Skeleton from '../components/negotiation/detail/Skeleton';

// Reused negotiation components
import StatusBanner from '../components/negotiation/StatusBanner';
import FollowUpPrompt from '../components/negotiation/FollowUpPrompt';
import FormBridgeCard from '../components/negotiation/FormBridgeCard';
import GuidedInput from '../components/negotiation/GuidedInput';
import NeedsInputPrompt from '../components/negotiation/NeedsInputPrompt';
import HandoffCard from '../components/negotiation/HandoffCard';
import ChatBubble from '../components/negotiation/ChatBubble';
import TypingIndicator from '../components/negotiation/TypingIndicator';
import ReportDraftModal from '../components/negotiation/ReportDraftModal';
import EndNegotiationDialog from '../components/negotiation/EndNegotiationDialog';
import PreferenceEditor from '../components/negotiation/PreferenceEditor';
import EditDraftModal from '../components/negotiation/EditDraftModal';

// Page-level chrome (shared across authenticated screens)
import DesktopHeader from '../components/common/DesktopHeader';
import ScreenHeader from '../components/common/ScreenHeader';
import LogoWhite from '../assets/images/logo.svg';

// ---------- Breakpoints ----------
const BREAKPOINT_DESKTOP = 1024;
const BREAKPOINT_TABLET = 768;

// ---------- Styled layout primitives ----------

// Outer screen container.
//
// Web-only app (CLAUDE.md rule), so we use 100dvh with 100vh fallback for older
// iOS Safari. `position: relative` is mandatory because `MobileDraftSheet`
// uses `position: absolute` against its nearest positioned ancestor.
const ScreenRoot = styled.View`
  flex: 1;
  background-color: #04060F;
  height: 100vh;
  height: 100dvh;
  position: relative;
  overflow: hidden;
`;

// Page-header-aware vertical column. Sits beneath the absolute DesktopHeader
// on desktop (paddingTop reserves space) and below the in-flow ScreenHeader on
// mobile. Centred with a max-width that mirrors NegotiationSetupScreen.
//
// We use a ScrollView so the chat stacks naturally below the listing card —
// no flex:1 chain stretching CenterCol to fill the viewport (which produced a
// big gap between the card and the action surface).
const MainColumn = styled.ScrollView.attrs(({ $isMobile }) => ({
  contentContainerStyle: {
    width: '100%',
    maxWidth: 940,
    alignSelf: 'center',
    paddingTop: $isMobile ? 8 : 88,
    paddingHorizontal: $isMobile ? 12 : 24,
    paddingBottom: $isMobile ? 96 : 24,
  },
}))`
  flex: 1;
`;

const BackRow = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  align-self: flex-start;
`;

const BackText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
`;

const CenterCol = styled.View`
  min-width: 0;
  flex-direction: column;
`;

// Was a styled.ScrollView when the screen had a fixed-height layout. The chat
// thread now flows inside MainColumn (which is itself the scroll container),
// so this is just a plain wrapper.
const ChatScroll = styled.View``;

const ChatInner = styled.View`
  padding: 12px 16px;
  gap: 4px;
`;

const BannerStackWrap = styled.View`
  padding: 8px 16px 0;
  gap: 8px;
`;

const DealerBarWrap = styled.View`
  flex-shrink: 1;
  max-height: 80%;
  border-top-width: 1px;
  border-top-color: ${REDESIGN_COLORS.borderSubtle};
  background-color: ${REDESIGN_COLORS.cardBg};
`;

const DealerBarPeek = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  gap: 12px;
`;

const DealerBarPeekText = styled.Text`
  flex: 1;
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const DealerBarBody = styled.ScrollView`
  flex-shrink: 1;
`;

const ActionWrap = styled.View`
  padding: 12px 16px 16px;
`;

const PlaceholderBanner = styled.View`
  padding: 12px 14px;
  border-radius: 8px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.scoreMedium};
  background-color: rgba(255, 123, 0, 0.10);
`;

const PlaceholderBannerText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 13px;
  line-height: 19px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const CenterMessage = styled.View`
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: 32px;
`;

const CenterText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textSecondary};
  text-align: center;
`;

const DraftPendingRow = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 10px;
  align-self: flex-start;
  padding: 8px 12px;
  margin-top: 6px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.04);
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const DraftPendingText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 12.5px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

// ---------- ListingHeaderCard ----------
//
// Horizontal card that replaces the old HeaderStrip. Modelled on the
// ListingCard from NegotiationSetupScreen: thumb + vehicle title on the left,
// price columns on the right. Status / round / autonomy badges sit under the
// title; back arrow + overflow ⋮ are inline so we don't need a separate header
// strip.
const fmtGBP = (n) =>
  n == null || isNaN(n) ? '--' : `£${Number(n).toLocaleString('en-GB')}`;

const buildVehicleLine = (snapshot) => {
  if (!snapshot) return 'Unknown vehicle';
  const { year, make, model, trim } = snapshot;
  const parts = [year, make, model, trim].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Unknown vehicle';
};

const buildDealerLine = (snapshot) => {
  if (!snapshot) return null;
  const dealer = snapshot.dealer;
  const name = dealer?.name || snapshot.dealer_name;
  const city = dealer?.city || snapshot.city;
  const parts = [name, city].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
};

const ListingHeaderCard = ({
  negotiation,
  currentRound,
  isMobile,
  onBack,
  onOpenListing,
  onOpenVdp,
  onEditPreferences,
  onEndNegotiation,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Outside-click + Escape close (web only — these listeners are no-ops on
  // native because document is undefined).
  useEffect(() => {
    if (!menuOpen) return undefined;
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return undefined;
    }
    const handleClick = (e) => {
      const node = menuAnchorRef.current?._node || menuAnchorRef.current;
      if (node && typeof node.contains === 'function' && node.contains(e.target)) {
        return;
      }
      closeMenu();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen, closeMenu]);

  const handleMenuItem = useCallback(
    (fn) => (e) => {
      e?.stopPropagation?.();
      closeMenu();
      fn?.();
    },
    [closeMenu]
  );

  const snapshot = negotiation?.listing_snapshot_at_start ?? null;
  const status = negotiation?.status;
  const autonomy = negotiation?.autonomy;
  const askingPrice = negotiation?.asking_price_at_start;
  const dealerHasEmail = !!negotiation?.dealer_email;
  const targetPrice = negotiation?.buyer_reservation_price;
  const photoUrl =
    snapshot?.photo_url ||
    (Array.isArray(snapshot?.photo_links) ? snapshot.photo_links[0] : null) ||
    null;
  // Model AI image baked into the snapshot at negotiation start (broken-photo-model-fallback spec).
  const modelImageUrl = snapshot?.modelImageUrl || null;

  const vehicleLine = buildVehicleLine(snapshot);
  const dealerLine = buildDealerLine(snapshot);
  const statusEntry = status && STATUS_LABELS[status] ? STATUS_LABELS[status] : null;
  const isGuided = autonomy === 'guided';
  const isAuto = autonomy === 'auto' || autonomy === 'assisted';

  return (
    <Pressable
      onPress={onOpenListing}
      disabled={!onOpenListing}
      style={({ hovered }) => [
        lhcStyles.card,
        isMobile && lhcStyles.cardMobile,
        onOpenListing && hovered && lhcStyles.cardHover,
      ]}
      accessibilityRole={onOpenListing ? 'link' : undefined}
      accessibilityLabel={onOpenListing ? 'Open the original listing' : undefined}
    >
      <View style={lhcStyles.topRow}>
        {/* Back arrow (mobile only — desktop has the BackRow above) */}
        {isMobile && onBack ? (
          <Pressable
            onPress={onBack}
            style={lhcStyles.iconBtn}
            accessibilityLabel="Go back"
            accessibilityRole="button"
          >
            <MaterialCommunityIcons
              name="arrow-left"
              size={20}
              color={REDESIGN_COLORS.textPrimary}
            />
          </Pressable>
        ) : null}

        {/* Thumb */}
        <View style={[lhcStyles.thumb, isMobile && lhcStyles.thumbMobile]}>
          {(photoUrl || modelImageUrl) ? (
            <FallbackImage
              primitive="expo"
              dealerPhotoUrl={photoUrl}
              modelImageUrl={modelImageUrl}
              style={StyleSheet.absoluteFill}
              contentFit="cover"
              cachePolicy="disk"
            />
          ) : (
            <View style={[StyleSheet.absoluteFill, lhcStyles.thumbPlaceholder]}>
              <MaterialCommunityIcons
                name="car-side"
                size={20}
                color="rgba(255,255,255,0.18)"
              />
            </View>
          )}
        </View>

        {/* Title + meta */}
        <View style={lhcStyles.body}>
          <Text
            style={[lhcStyles.title, isMobile && lhcStyles.titleMobile]}
            numberOfLines={1}
          >
            {vehicleLine}
          </Text>
          {dealerLine ? (
            <Text style={lhcStyles.dealer} numberOfLines={1}>
              {dealerLine}
            </Text>
          ) : null}
          <View style={lhcStyles.badgeRow}>
            {statusEntry ? (
              <View
                style={[lhcStyles.statusPill, { backgroundColor: statusEntry.color }]}
              >
                <Text style={lhcStyles.statusPillText}>{statusEntry.label}</Text>
              </View>
            ) : null}
            {currentRound > 0 ? (
              <View style={lhcStyles.roundBadge}>
                <Text style={lhcStyles.roundBadgeText}>{`Round ${currentRound}`}</Text>
              </View>
            ) : null}
            {isGuided ? (
              <MaterialCommunityIcons
                name="clipboard-text-outline"
                size={16}
                color={REDESIGN_COLORS.textSecondary}
                accessibilityLabel="Guided mode"
              />
            ) : isAuto ? (
              <MaterialCommunityIcons
                name="robot-outline"
                size={16}
                color={REDESIGN_COLORS.textSecondary}
                accessibilityLabel="Auto mode"
              />
            ) : null}
          </View>
        </View>

        {/* Price columns (hidden on the narrowest mobile, shown below instead) */}
        {!isMobile ? (
          <>
            <View style={lhcStyles.priceCol}>
              <Text style={lhcStyles.priceLabel}>Asking</Text>
              <Text style={lhcStyles.priceValue}>{fmtGBP(askingPrice)}</Text>
            </View>
            <View style={[lhcStyles.priceCol, lhcStyles.priceColTarget]}>
              <Text style={lhcStyles.priceLabel}>Your target</Text>
              <Text style={[lhcStyles.priceValue, lhcStyles.priceValueTarget]}>
                {fmtGBP(targetPrice)}
              </Text>
            </View>
          </>
        ) : null}

        {/* Overflow ⋮ — inline menu anchored to the button */}
        <View ref={menuAnchorRef} style={lhcStyles.menuAnchor}>
          <Pressable
            onPress={(e) => {
              e?.stopPropagation?.();
              setMenuOpen((v) => !v);
            }}
            style={lhcStyles.iconBtn}
            accessibilityLabel="More options"
            accessibilityRole="button"
          >
            <MaterialCommunityIcons
              name="dots-vertical"
              size={20}
              color={REDESIGN_COLORS.textPrimary}
            />
          </Pressable>
          {menuOpen ? (
            <View
              style={lhcStyles.menuSheet}
              onStartShouldSetResponder={() => true}
            >
              {!dealerHasEmail && onOpenVdp ? (
                <Pressable
                  onPress={handleMenuItem(onOpenVdp)}
                  style={lhcStyles.menuItem}
                >
                  <MaterialCommunityIcons
                    name="open-in-new"
                    size={16}
                    color={REDESIGN_COLORS.textPrimary}
                  />
                  <Text style={lhcStyles.menuItemText}>Open VDP</Text>
                </Pressable>
              ) : null}
              {onEditPreferences ? (
                <Pressable
                  onPress={handleMenuItem(onEditPreferences)}
                  style={lhcStyles.menuItem}
                >
                  <MaterialCommunityIcons
                    name="tune"
                    size={16}
                    color={REDESIGN_COLORS.textPrimary}
                  />
                  <Text style={lhcStyles.menuItemText}>Edit preferences</Text>
                </Pressable>
              ) : null}
              {onEndNegotiation ? (
                <>
                  <View style={lhcStyles.menuDivider} />
                  <Pressable
                    onPress={handleMenuItem(onEndNegotiation)}
                    style={[lhcStyles.menuItem, lhcStyles.menuItemDanger]}
                  >
                    <MaterialCommunityIcons
                      name="close-circle-outline"
                      size={16}
                      color="#ef4444"
                    />
                    <Text style={[lhcStyles.menuItemText, lhcStyles.menuItemTextDanger]}>
                      End negotiation
                    </Text>
                  </Pressable>
                </>
              ) : null}
            </View>
          ) : null}
        </View>
      </View>

      {/* Mobile: price columns stacked under the row so the title can breathe */}
      {isMobile ? (
        <View style={lhcStyles.mobilePriceRow}>
          <View style={lhcStyles.priceColMobile}>
            <Text style={lhcStyles.priceLabel}>Asking</Text>
            <Text style={lhcStyles.priceValue}>{fmtGBP(askingPrice)}</Text>
          </View>
          <View style={[lhcStyles.priceColMobile, lhcStyles.priceColTarget]}>
            <Text style={lhcStyles.priceLabel}>Your target</Text>
            <Text style={[lhcStyles.priceValue, lhcStyles.priceValueTarget]}>
              {fmtGBP(targetPrice)}
            </Text>
          </View>
        </View>
      ) : null}
    </Pressable>
  );
};

const lhcStyles = StyleSheet.create({
  card: {
    flexDirection: 'column',
    padding: 14,
    backgroundColor: 'rgba(255,255,255,0.035)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 14,
    marginBottom: 12,
    overflow: 'visible',
    // The overflow ⋮ menu is position:absolute inside this card; without a
    // stacking context here it paints behind later siblings (banners, chat
    // column) because RN-Web Views default to position:relative and stack by
    // source order. Lift the whole card above those siblings.
    position: 'relative',
    zIndex: 100,
  },
  cardMobile: { padding: 10, borderRadius: 12, marginBottom: 8 },
  cardHover: { borderColor: 'rgba(255,255,255,0.20)', cursor: 'pointer' },
  menuAnchor: { position: 'relative', zIndex: 60 },
  menuSheet: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 6,
    minWidth: 200,
    backgroundColor: '#2A3441',
    opacity: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    padding: 6,
    zIndex: 50,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.55,
    shadowRadius: 32,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  menuItemDanger: { backgroundColor: 'rgba(239, 68, 68, 0.08)' },
  menuItemText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: '#FFFFFF',
  },
  menuItemTextDanger: { color: '#ef4444' },
  menuDivider: {
    height: 1,
    backgroundColor: 'rgba(255,255,255,0.10)',
    marginVertical: 4,
  },
  topRow: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  iconBtn: { padding: 4 },
  thumb: {
    width: 96,
    height: 64,
    borderRadius: 10,
    backgroundColor: '#1A1A1A',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  thumbMobile: { width: 64, height: 48, borderRadius: 8 },
  thumbPlaceholder: { alignItems: 'center', justifyContent: 'center' },
  body: { flex: 1, minWidth: 0, gap: 3 },
  title: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    color: '#FFFFFF',
    letterSpacing: -0.05,
  },
  titleMobile: { fontSize: 14.5 },
  dealer: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: 'rgba(255,255,255,0.55)',
  },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    flexWrap: 'wrap',
  },
  statusPill: { paddingVertical: 2, paddingHorizontal: 8, borderRadius: 20, opacity: 0.9 },
  statusPillText: { fontFamily: 'Satoshi-Bold', fontSize: 11, color: '#FFFFFF' },
  roundBadge: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  roundBadgeText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 11,
    color: 'rgba(255,255,255,0.7)',
  },
  priceCol: {
    alignItems: 'flex-end',
    gap: 4,
    paddingLeft: 16,
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255,255,255,0.06)',
    minWidth: 96,
  },
  priceColTarget: { paddingLeft: 16 },
  priceLabel: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 10,
    letterSpacing: 0.8,
    color: 'rgba(255,255,255,0.45)',
    textTransform: 'uppercase',
  },
  priceValue: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 20,
    color: '#FFFFFF',
    letterSpacing: -0.3,
  },
  priceValueTarget: { color: '#B3CCFF' },
  mobilePriceRow: {
    flexDirection: 'row',
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.06)',
    gap: 16,
  },
  priceColMobile: {
    flex: 1,
    alignItems: 'flex-start',
    gap: 4,
  },
});

// ---------- Banner-stack -> component mapping ----------
//
// `kind` strings come from `buildBannerStack`.
// StatusBanner today does not accept a `kind` prop, but its internal logic
// already covers `listing_removed` and `auto_paused` cases by reading the
// negotiation row directly. We pass `negotiation` for those branches and
// render an inline placeholder for the kinds StatusBanner doesn't handle yet.
//
// TODO (post-27b): consolidate banner copy by extending StatusBanner to take
// an explicit `kind` so the orchestrator does not need placeholders. For now
// the placeholders are minimal and accurate.
const renderBanner = ({
  banner,
  negotiation,
  formBridgeDismissed,
  approvedScriptText,
  onFormBridgeDismiss,
  onSendFollowUp,
  onTryCalling,
  onGuidedSubmit,
  onFindSimilarListing,
  onEndNegotiation,
}) => {
  const { kind } = banner;

  // StatusBanner already covers listing_removed via auto_pause_reason.
  // Audit C1 (2026-04-30): pass action handlers through so the banner can
  // surface the "Find a similar listing" + "End this negotiation" CTAs.
  if (kind === 'listing_removed') {
    return (
      <StatusBanner
        key="banner-listing-removed"
        negotiation={negotiation}
        onFindSimilarListing={onFindSimilarListing}
        onEndNegotiation={onEndNegotiation}
      />
    );
  }

  if (kind === 'auto_paused_generic') {
    return (
      <PlaceholderBanner key="banner-auto-paused-generic">
        <PlaceholderBannerText>
          Negotiation auto-paused. We will resume once the underlying issue is resolved.
        </PlaceholderBannerText>
      </PlaceholderBanner>
    );
  }

  if (kind === 'paused_user') {
    return (
      <PlaceholderBanner key="banner-paused-user">
        <PlaceholderBannerText>Negotiation paused.</PlaceholderBannerText>
      </PlaceholderBanner>
    );
  }

  if (kind === 'price_drop') {
    // StatusBanner reads `priceChangeEvent` directly. The selector emits
    // event_type==='price_drop' (legacy `listing_price_changed` rows are also
    // forwarded to StatusBanner via the negotiation row).
    return (
      <StatusBanner
        key={`banner-price-drop-${banner.props?.event?.id || 'event'}`}
        negotiation={negotiation}
        priceChangeEvent={banner.props?.event}
      />
    );
  }

  if (kind === 'stalled') {
    return (
      <FollowUpPrompt
        key="banner-stalled"
        negotiation={negotiation}
        onSendFollowUp={onSendFollowUp}
        onTryCalling={onTryCalling}
      />
    );
  }

  if (kind === 'form_bridge') {
    // Three-state machine for the dismissal flag:
    //   null  -> still hydrating from AsyncStorage; render nothing.
    //   true  -> dismissed; render nothing.
    //   false -> not dismissed; render the card.
    if (formBridgeDismissed === null) return null;
    if (formBridgeDismissed === true) return null;
    return (
      <FormBridgeCard
        key="banner-form-bridge"
        listingUrl={negotiation?.listing_snapshot_at_start?.vdp_url}
        aliasEmail={negotiation?.alias_email}
        approvedScript={approvedScriptText}
        onDismiss={onFormBridgeDismiss}
      />
    );
  }

  // 'post_call_coaching' is rendered as a collapsible bottom bar
  // (DealerMessageBar) outside the banner stack — see renderCenter().

  return null;
};

// DealerMessageBar — collapsible bottom bar that mounts GuidedInput inline.
// Replaces the old top-of-screen post_call_coaching banner so users can log
// a dealer reply (phone, in person, or via the dealer's contact form) at any
// point without it dominating the chat header.
//
//   collapsed: "I have a message from the dealer"  ▲
//   expanded:  same row with caret-down, plus the 3-step GuidedInput form.
const DealerMessageBar = ({ negotiationId, onSubmit }) => {
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded((v) => !v), []);
  const handleSubmitted = useCallback(
    (msg) => {
      setExpanded(false);
      onSubmit?.(msg);
    },
    [onSubmit]
  );
  return (
    <DealerBarWrap>
      <DealerBarPeek
        onPress={toggle}
        accessibilityRole="button"
        accessibilityLabel={
          expanded ? 'Collapse dealer message form' : 'Open dealer message form'
        }
      >
        <DealerBarPeekText>I have a message from the dealer</DealerBarPeekText>
        <MaterialCommunityIcons
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={20}
          color={REDESIGN_COLORS.textPrimary}
        />
      </DealerBarPeek>
      {expanded ? (
        <DealerBarBody
          contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 16 }}
          showsVerticalScrollIndicator={false}
        >
          <GuidedInput
            negotiationId={negotiationId}
            onSubmit={handleSubmitted}
          />
        </DealerBarBody>
      ) : null}
    </DealerBarWrap>
  );
};

// ---------- Component ----------

const NegotiationChatScreen = ({ negotiationId }) => {
  const { session, logout } = useAuth();
  const { width } = useWindowDimensions();
  const router = useRouter();
  const navLinks = useNavLinks({ logout, session });

  const isMobile = width < BREAKPOINT_TABLET;

  const {
    messages,
    negotiation,
    isLoading: realtimeLoading,
  } = useNegotiationRealtime(negotiationId, session);
  const { events, isLoading: eventsLoading } = useNegotiationEvents(
    negotiationId,
    session
  );

  // ---------- Derived selector state ----------
  const activeDrafts = useMemo(
    () => selectActiveDrafts(messages || []),
    [messages]
  );

  const surface = useMemo(
    () => selectActionSurface(negotiation, activeDrafts),
    [negotiation, activeDrafts]
  );

  // FormBridge dismissal — three-state machine:
  //   null  -> still loading from AsyncStorage; FormBridgeCard returns null.
  //   false -> not dismissed; render the card.
  //   true  -> dismissed; do not render.
  const [formBridgeDismissed, setFormBridgeDismissed] = useState(null);

  // Hydrate dismissal flag from AsyncStorage on mount / when negotiation id
  // changes. Key is namespaced per-negotiation so different threads stay
  // independent.
  useEffect(() => {
    let cancelled = false;
    const negId = negotiation?.id;
    if (!negId) return;
    const key = `${FORM_BRIDGE_DISMISSAL_KEY_PREFIX}${negId}`;
    (async () => {
      try {
        const stored = await AsyncStorage.getItem(key);
        if (cancelled) return;
        setFormBridgeDismissed(stored === '1');
      } catch (err) {
        logger.error(
          '[NegotiationChat] FormBridge dismissal hydrate failed:',
          err
        );
        if (!cancelled) setFormBridgeDismissed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [negotiation?.id]);

  // Lazy cleanup: when the negotiation reaches a terminal status, drop the
  // dismissal flag from AsyncStorage so a future negotiation reusing the same
  // id (extremely unlikely) does not inherit it. This matches the spec's
  // "after consumption" behaviour.
  useEffect(() => {
    const negId = negotiation?.id;
    const status = negotiation?.status;
    if (!negId || !status) return;
    if (status === 'closed' || status === 'abandoned') {
      const key = `${FORM_BRIDGE_DISMISSAL_KEY_PREFIX}${negId}`;
      AsyncStorage.removeItem(key).catch((err) => {
        logger.error(
          '[NegotiationChat] FormBridge dismissal cleanup failed:',
          err
        );
      });
    }
  }, [negotiation?.id, negotiation?.status]);

  const banners = useMemo(
    () =>
      buildBannerStack(negotiation, events, messages, {
        formBridge: formBridgeDismissed,
      }),
    [negotiation, events, messages, formBridgeDismissed]
  );

  // ---------- Mobile bottom-sheet expansion ----------
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const expandSheet = useCallback(() => setSheetExpanded(true), []);
  const collapseSheet = useCallback(() => setSheetExpanded(false), []);
  const autoOpenedDraftRef = useRef(null);

  // ---------- Selected draft version (V1/V2/...) ----------
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const lastSeenForRoundRef = useRef({ round: null, maxVersion: 0 });

  useEffect(() => {
    if (activeDrafts.length === 0) {
      lastSeenForRoundRef.current = { round: null, maxVersion: 0 };
      return;
    }
    const latest = activeDrafts[activeDrafts.length - 1];
    const round = latest.round_number || 0;
    const maxVersion = latest.draft_version || 0;

    if (lastSeenForRoundRef.current.round !== round) {
      lastSeenForRoundRef.current = { round, maxVersion };
      setSelectedDraftId(latest.id);
      return;
    }
    if (maxVersion > lastSeenForRoundRef.current.maxVersion) {
      lastSeenForRoundRef.current.maxVersion = maxVersion;
      setSelectedDraftId(latest.id);
      return;
    }
    if (
      !selectedDraftId ||
      !activeDrafts.find((d) => d.id === selectedDraftId)
    ) {
      setSelectedDraftId(latest.id);
    }
  }, [activeDrafts, selectedDraftId]);

  // ---------- Auto-expand mobile draft sheet on new draft ----------
  // When a new actionable draft arrives (surface flips to 'draft' or 'guardrail'),
  // expand the sheet once so the user sees it. Tracked by draft id so manual
  // collapses are not fought, and subsequent rounds re-trigger on the new id.
  useEffect(() => {
    if (!isMobile) return;
    if (surface !== 'draft' && surface !== 'guardrail') return;
    if (!selectedDraftId) return;
    if (autoOpenedDraftRef.current === selectedDraftId) return;
    autoOpenedDraftRef.current = selectedDraftId;
    setSheetExpanded(true);
  }, [isMobile, surface, selectedDraftId]);

  // ---------- Regen counter ----------
  const regenCount = useMemo(() => {
    if (!messages || !negotiation) return 0;
    const draftRows = (messages || []).filter((m) => m.role === 'agent_draft');
    const lastRound = draftRows.reduce(
      (max, m) => Math.max(max, m.round_number || 0),
      0
    );
    return draftRows.filter((m) => (m.round_number || 0) === lastRound).length;
  }, [messages, negotiation]);
  const regenMax = NEGOTIATION_CONFIG.MAX_DRAFTS_PER_ROUND;

  // ---------- Report modal state ----------
  const [reportingDraftId, setReportingDraftId] = useState(null);

  // ---------- Draft action callbacks ----------
  //
  // TODO (Task 27c): Wire these to the full edit/send-confirmation flow.
  // The previous screen had a SendConfirmation modal + per-draft edit modal
  // + supabase-direct status writes. For 27b we use the simplest signature
  // that exercises apiService.requestNegotiationAction so the surface is
  // observable end-to-end during smoke tests.

  // Approve flow:
  //   1. Discard sibling drafts (V1/V2 when approving V3, plus any stale
  //      drafts from earlier rounds that never got cleaned up).
  //   2. Flip the chosen draft from 'draft' → 'approved'. The worker's
  //      sendApprovedDraft job rejects messages whose status isn't in
  //      ['approved', 'auto_approved', 'retry_pending'], so this step is
  //      mandatory.
  //   3. Guided autonomy: stop here (no email send — the user dispatches
  //      manually). Assisted/auto: enqueue send_approved_draft.
  const handleApprove = useCallback(
    async (draftId) => {
      if (!draftId || !negotiation?.id) return;
      let approvedDraftId = null;
      let supabaseClient = null;
      try {
        supabaseClient = (await import('../utils/supabaseClient')).default;

        // Step 1: discard every other active draft (any round, any version)
        const siblingIds = (activeDrafts || [])
          .filter((d) => d.id !== draftId)
          .map((d) => d.id);
        if (siblingIds.length > 0) {
          const { error: discardErr } = await supabaseClient
            .from('negotiation_messages')
            .update({ status: 'discarded' })
            .in('id', siblingIds);
          if (discardErr) {
            logger.warn(
              '[NegotiationChat] approve: sibling discard failed',
              discardErr
            );
          }
        }

        // Step 2: flip chosen draft to 'approved'
        const { error: approveErr } = await supabaseClient
          .from('negotiation_messages')
          .update({ status: 'approved' })
          .eq('id', draftId);
        if (approveErr) throw approveErr;
        approvedDraftId = draftId;

        // Step 3: guided mode is a no-send acknowledgement
        if (negotiation.autonomy === 'guided') return;

        await apiService.requestNegotiationAction('send_approved_draft', {
          negotiation_id: negotiation.id,
          message_id: draftId,
        });
      } catch (err) {
        logger.error('[NegotiationChat] approve failed:', err);
        if (approvedDraftId && supabaseClient && negotiation?.autonomy !== 'guided') {
          try {
            await supabaseClient
              .from('negotiation_messages')
              .update({ status: 'draft' })
              .eq('id', approvedDraftId)
              .eq('status', 'approved');
          } catch (rollbackErr) {
            logger.warn(
              '[NegotiationChat] approve rollback failed:',
              rollbackErr
            );
          }
        }
        // monetization-step-6 Phase D sonnet S2: surface FAIL_CLOSED 503
        // (transient infra) and dealer_messages cap-hit 403 instead of
        // silently swallowing. The user sees a toast / alert so they
        // know the send didn't succeed and can retry.
        const code = err?.code || '';
        const body = err?.body;
        const message = body?.message || err?.message;
        if (body?.transient) {
          Alert.alert('Send failed', message || 'Service temporarily unavailable. Please retry.');
        } else if (code && code.startsWith('cap_')) {
          Alert.alert('Daily limit', message || 'Message limit reached.');
        }
      }
    },
    [negotiation?.id, negotiation?.autonomy, activeDrafts]
  );

  // ---------- Edit draft modal ----------
  // The user picks "Edit" from the DraftBlock overflow ▾ → we open the
  // modal pre-populated with the draft's content. On Save, we insert a
  // user_written row carrying the edited content, discard every active
  // draft (including the original), and dispatch send_approved_draft.
  const [editingDraft, setEditingDraft] = useState(null);

  const handleEdit = useCallback(
    (draftId) => {
      const draft = (messages || []).find((m) => m.id === draftId);
      if (!draft) return;
      setEditingDraft({
        id: draft.id,
        subject: draft.subject || null,
        content: draft.content || '',
        round_number: draft.round_number || 1,
      });
    },
    [messages]
  );

  const handleCloseEdit = useCallback(() => setEditingDraft(null), []);

  const handleSaveEdit = useCallback(
    async ({ subject, content }) => {
      if (!editingDraft || !negotiation?.id) return;
      const supabaseClient = (await import('../utils/supabaseClient')).default;
      const { data: userData } = await supabaseClient.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) throw new Error('No authenticated user');

      // INSERT user_written row. Inherit subject + round_number from the
      // original draft so threading and round bookkeeping stay aligned.
      const { data: insertData, error: insertErr } = await supabaseClient
        .from('negotiation_messages')
        .insert({
          negotiation_id: negotiation.id,
          user_id: userId,
          role: 'user_written',
          status: 'approved',
          content,
          subject: subject || editingDraft.subject || null,
          round_number: editingDraft.round_number,
        })
        .select('id')
        .single();
      if (insertErr) throw insertErr;
      const newMessageId = insertData.id;

      // Discard every active draft in this negotiation (any round/version).
      const draftIdsToDiscard = (activeDrafts || []).map((d) => d.id);
      if (draftIdsToDiscard.length > 0) {
        await supabaseClient
          .from('negotiation_messages')
          .update({ status: 'discarded' })
          .in('id', draftIdsToDiscard);
      }

      // Dispatch the send.
      await apiService.requestNegotiationAction('send_approved_draft', {
        negotiation_id: negotiation.id,
        message_id: newMessageId,
      });
    },
    [editingDraft, negotiation?.id, activeDrafts]
  );

  // Inline-edit save handler invoked from DraftBlock when the user taps the
  // suggested message, edits it in place, and presses Save & send. Mirrors
  // handleSaveEdit but takes the draft id directly so we don't need to stage
  // anything via the modal.
  const handleInlineSaveEdit = useCallback(
    async (draftId, { subject, content }) => {
      if (!negotiation?.id) return;
      const sourceDraft = (messages || []).find((m) => m.id === draftId);
      if (!sourceDraft) return;
      const supabaseClient = (await import('../utils/supabaseClient')).default;
      const { data: userData } = await supabaseClient.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) throw new Error('No authenticated user');

      const { data: insertData, error: insertErr } = await supabaseClient
        .from('negotiation_messages')
        .insert({
          negotiation_id: negotiation.id,
          user_id: userId,
          role: 'user_written',
          status: 'approved',
          content,
          subject: subject || sourceDraft.subject || null,
          round_number: sourceDraft.round_number || 1,
        })
        .select('id')
        .single();
      if (insertErr) throw insertErr;
      const newMessageId = insertData.id;

      const draftIdsToDiscard = (activeDrafts || []).map((d) => d.id);
      if (draftIdsToDiscard.length > 0) {
        await supabaseClient
          .from('negotiation_messages')
          .update({ status: 'discarded' })
          .in('id', draftIdsToDiscard);
      }

      await apiService.requestNegotiationAction('send_approved_draft', {
        negotiation_id: negotiation.id,
        message_id: newMessageId,
      });
    },
    [negotiation?.id, messages, activeDrafts]
  );

  const handleRegenerate = useCallback(async () => {
    if (!negotiation?.id) return;
    try {
      await apiService.requestNegotiationAction('regenerate_draft', {
        negotiation_id: negotiation.id,
      });
    } catch (err) {
      logger.error('[NegotiationChat] regenerate failed:', err);
    }
  }, [negotiation?.id]);

  const handleDiscard = useCallback(
    async (draftId) => {
      // No dedicated discard action exists today; the previous screen wrote
      // status='discarded' directly via supabase. Mirror that here as a
      // best-effort minimal implementation.
      // TODO (Task 27c): centralise this through a worker action when one lands.
      if (!draftId) return;
      try {
        const supabaseClient = (await import('../utils/supabaseClient'))
          .default;
        await supabaseClient
          .from('negotiation_messages')
          .update({ status: 'discarded' })
          .eq('id', draftId);
      } catch (err) {
        logger.error('[NegotiationChat] discard failed:', err);
      }
    },
    []
  );

  const handleReport = useCallback((draftId) => {
    setReportingDraftId(draftId);
  }, []);

  // ---------- deal_reached_prompt callbacks ----------
  // The worker emits a system message with structured_input.kind ===
  // 'deal_reached_prompt' when it detects dealer acceptance. ChatBubble
  // surfaces these as two CTAs: "Wrap up at £X" and "Keep negotiating".

  const handleWrapUpDeal = useCallback(async () => {
    if (!negotiation?.id) return;
    try {
      await apiService.requestNegotiationAction('complete_deal', {
        negotiation_id: negotiation.id,
      });
    } catch (err) {
      // 409 = negotiation is already in a terminal state (closed, abandoned,
      // handed_off, deal_reached). The realtime subscription will reconcile
      // the UI; treat as a no-op rather than burning a user-visible error.
      if (err?.status === 409) {
        logger.info('[NegotiationChat] complete_deal: state_conflict (already terminal); UI will reconcile via realtime', err?.body);
        return;
      }
      logger.error('[NegotiationChat] complete_deal failed:', err);
      Alert.alert('Could not confirm', err?.message || 'Could not confirm the deal. Please try again.');
    }
  }, [negotiation?.id]);

  const handleKeepNegotiating = useCallback(async () => {
    if (!negotiation?.id) return;
    try {
      // force_continue:true tells the worker to override the deal_reached
      // strategy and draft a follow-up that pushes for one more concession.
      await apiService.requestNegotiationAction('generate_draft', {
        negotiation_id: negotiation.id,
        force_continue: true,
      });
    } catch (err) {
      logger.error('[NegotiationChat] keep_negotiating failed:', err);
    }
  }, [negotiation?.id]);

  // FollowUpPrompt callbacks.
  // generate_followup is fired through requestNegotiationAction with the
  // (action, payload) signature documented in apiService.js (line ~274).
  const handleSendFollowUp = useCallback(async () => {
    if (!negotiation?.id) return;
    try {
      await apiService.requestNegotiationAction('generate_followup', {
        negotiation_id: negotiation.id,
      });
    } catch (err) {
      logger.error('[NegotiationChat] send follow-up failed:', err);
    }
  }, [negotiation?.id]);

  // Phone coaching flow is not yet wired in this branch. Leaving this as a
  // no-op (with a TODO) per the task spec; FollowUpPrompt still works for the
  // primary follow-up CTA.
  const handleTryCalling = useCallback(() => {
    // TODO: wire call-coaching modal when the call coaching flow lands.
    logger.warn(
      '[NegotiationChat] onTryCalling: call-coaching flow not wired in this branch'
    );
  }, []);

  // FormBridge dismiss handler — persist the flag so the card stays dismissed
  // across reloads for this specific negotiation.
  const handleFormBridgeDismiss = useCallback(async () => {
    setFormBridgeDismissed(true);
    const negId = negotiation?.id;
    if (!negId) return;
    const key = `${FORM_BRIDGE_DISMISSAL_KEY_PREFIX}${negId}`;
    try {
      await AsyncStorage.setItem(key, '1');
    } catch (err) {
      logger.error(
        '[NegotiationChat] FormBridge dismissal persist failed:',
        err
      );
    }
  }, [negotiation?.id]);

  // Waiting-for-draft flag. GuidedInput (or DealerMessageBar) submits a
  // dealer_reported message; the worker then queues a draft generation that
  // can take 5-15s. Without a visual cue between those events the chat sits
  // silent and the user assumes nothing happened. We anchor on the draft ids
  // present at submit time and clear the flag as soon as a *new* draft id
  // appears in activeDrafts (or the negotiation reaches a terminal status, or
  // the 60s safety timeout fires).
  const [waitingForDraft, setWaitingForDraft] = useState(false);
  const waitingAnchorIdsRef = useRef(new Set());
  const waitingTimeoutRef = useRef(null);

  useEffect(() => {
    if (!waitingForDraft) return;
    const anchored = waitingAnchorIdsRef.current;
    const newDraftAppeared = activeDrafts.some((d) => !anchored.has(d.id));
    if (newDraftAppeared) {
      setWaitingForDraft(false);
      if (waitingTimeoutRef.current) {
        clearTimeout(waitingTimeoutRef.current);
        waitingTimeoutRef.current = null;
      }
    }
  }, [activeDrafts, waitingForDraft]);

  useEffect(
    () => () => {
      if (waitingTimeoutRef.current) clearTimeout(waitingTimeoutRef.current);
    },
    []
  );

  const handleGuidedSubmit = useCallback(() => {
    waitingAnchorIdsRef.current = new Set(
      (activeDrafts || []).map((d) => d.id)
    );
    setWaitingForDraft(true);
    if (waitingTimeoutRef.current) clearTimeout(waitingTimeoutRef.current);
    // Safety backstop in case the realtime subscription drops the new-draft
    // event — matches DraftBlock.handleRegenerate's 60s ceiling.
    waitingTimeoutRef.current = setTimeout(() => {
      setWaitingForDraft(false);
      waitingTimeoutRef.current = null;
    }, 60000);
  }, [activeDrafts]);

  const handleNeedsInputSubmit = useCallback(
    async (text) => {
      const trimmed = (text || '').trim();
      if (!trimmed || !negotiation?.id) return;

      const needsInputDraft = (activeDrafts || [])
        .slice()
        .reverse()
        .find((d) => d.status === 'needs_user_input');

      try {
        const userId = session?.user?.id;
        if (!userId) throw new Error('No authenticated user');

        const structuredInput = {
          kind: 'needs_input_response',
          source_message_id: needsInputDraft?.id || null,
          text: trimmed,
        };

        const { data: inserted, error: insertErr } = await supabase
          .from('negotiation_messages')
          .insert({
            negotiation_id: negotiation.id,
            user_id: userId,
            role: 'user_written',
            status: 'received',
            content: trimmed,
            round_number:
              needsInputDraft?.round_number || negotiation.current_round || 1,
            structured_input: structuredInput,
          })
          .select('id')
          .single();
        if (insertErr) throw insertErr;

        if (needsInputDraft?.id) {
          const { error: discardErr } = await supabase
            .from('negotiation_messages')
            .update({ status: 'discarded' })
            .eq('id', needsInputDraft.id);
          if (discardErr) {
            logger.warn(
              '[NegotiationChat] needs_input: source draft discard failed',
              discardErr
            );
          }
        }

        await supabase
          .from('negotiations')
          .update({ last_user_action_at: new Date().toISOString() })
          .eq('id', negotiation.id);

        await apiService.requestNegotiationAction('generate_draft', {
          negotiation_id: negotiation.id,
          triggered_by_message_id: inserted.id,
        });
      } catch (err) {
        logger.error('[NegotiationChat] needs_input submit failed:', err);
        const message = err?.body?.message || err?.message;
        Alert?.alert?.(
          'Could not submit',
          message || 'Something went wrong. Please try again.'
        );
        throw err;
      }
    },
    [
      activeDrafts,
      negotiation?.id,
      negotiation?.current_round,
      session?.user?.id,
    ]
  );

  // ---------- HeaderStrip handlers ----------
  // Back navigation: prefer router.back() so the user lands wherever they came
  // from (queue, deep link, etc.); fall back to /negotiations if there is no
  // history (direct-link entry).
  const handleBack = useCallback(() => {
    try {
      if (router?.canGoBack && router.canGoBack()) {
        router.back();
      } else {
        router.replace('/negotiations');
      }
    } catch (err) {
      logger.error('[NegotiationChat] back navigation failed:', err);
    }
  }, [router]);

  // End-negotiation confirmation dialog. The overflow menu itself is now owned
  // by ListingHeaderCard (anchored to the ⋮ button).
  const [isEndDialogOpen, setIsEndDialogOpen] = useState(false);

  const handleEndNegotiationFromMenu = useCallback(() => {
    setIsEndDialogOpen(true);
  }, []);

  // Audit C1 (2026-04-30): "Find a similar listing" CTA on the listing-removed
  // banner. Closes the current negotiation as `sold_to_other` (the most accurate
  // semantic when auto_pause_reason='listing_removed') and routes the user to
  // the cars feed so they can pick a fresh listing and start a new negotiation.
  // The user's preferences are not auto-carried-over for the MVP; they re-enter
  // priorities/reservation when starting the new neg. A backend "pivot_listing"
  // action that preserves the same negotiation row + history is a follow-up.
  const handleFindSimilarListing = useCallback(async () => {
    if (!negotiation?.id) return;
    try {
      // RLS forbids client UPDATEs on `status`; route through the edge fn so
      // the close actually applies (see end_negotiation handler).
      await apiService.requestNegotiationAction('end_negotiation', {
        negotiation_id: negotiation.id,
        outcome: 'find_similar',
      });
      router.replace('/home');
    } catch (err) {
      // 409 = already terminal; still navigate so the UI matches the user's
      // intent. Anything else: log + stay put.
      if (err?.status === 409) {
        router.replace('/home');
        return;
      }
      logger.error('[NegotiationChat] Find similar listing failed:', err);
    }
  }, [negotiation?.id, router]);

  // 'deal_reached' -> complete_deal action (sets deal_reached + queues summary).
  // 'abandoned' / 'closed' / 'sold_to_other' -> end_negotiation action: the
  // edge fn flips status with service role (RLS forbids the client from
  // changing `status` directly — a bare client UPDATE silently affects zero
  // rows) and inserts the system message in the same call. Courtesy follow-up
  // for assisted abandonment still gets enqueued from here.
  const handleEndNegotiationConfirm = useCallback(
    async (outcome) => {
      setIsEndDialogOpen(false);
      if (!negotiation?.id) return;
      try {
        if (outcome === 'deal_reached') {
          await apiService.requestNegotiationAction('complete_deal', {
            negotiation_id: negotiation.id,
          });
          return;
        }

        await apiService.requestNegotiationAction('end_negotiation', {
          negotiation_id: negotiation.id,
          outcome,
        });

        if (outcome === 'abandoned' && negotiation?.autonomy === 'assisted') {
          try {
            await apiService.requestNegotiationAction('generate_followup', {
              negotiation_id: negotiation.id,
              is_courtesy_close: true,
            });
          } catch (err) {
            logger.warn(
              '[NegotiationChat] courtesy close follow-up failed:',
              err
            );
          }
        }
      } catch (err) {
        // 409 from end_negotiation / complete_deal = already terminal; the
        // realtime subscription will reconcile the UI. Other errors surface
        // to the user so a tap on "End negotiation" isn't silently swallowed.
        if (err?.status === 409) {
          logger.info('[NegotiationChat] End negotiation: state_conflict (already terminal); UI will reconcile via realtime', err?.body);
          return;
        }
        logger.error('[NegotiationChat] End negotiation failed:', err);
        Alert.alert('Could not end negotiation', err?.message || 'Please try again in a moment.');
      }
    },
    [negotiation?.id, negotiation?.autonomy]
  );

  const handleOpenVdp = useCallback(() => {
    const url =
      negotiation?.vdp_url ||
      negotiation?.listing_snapshot_at_start?.vdp_url ||
      null;
    if (!url) {
      logger.warn('[NegotiationChat] No VDP URL on negotiation');
      return;
    }
    Linking.openURL(url).catch((err) => {
      logger.error('[NegotiationChat] Failed to open VDP url:', err);
    });
  }, [negotiation?.vdp_url, negotiation?.listing_snapshot_at_start?.vdp_url]);

  // The negotiations table doesn't store the feed row id; resolve it lazily by
  // looking up user_listing_feeds for this user + vdp_url (the same key used by
  // the setup screen to detect existing negotiations). Cached locally so the
  // card-click navigation is instant after the first paint.
  const negotiationVdpUrl =
    negotiation?.vdp_url ||
    negotiation?.listing_snapshot_at_start?.vdp_url ||
    null;
  const [feedEntryIdForListing, setFeedEntryIdForListing] = useState(null);
  useEffect(() => {
    if (!session?.user?.id || !negotiationVdpUrl) {
      setFeedEntryIdForListing(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const supabaseClient = (await import('../utils/supabaseClient')).default;
        const { data, error } = await supabaseClient
          .from('user_listing_feeds')
          .select('id')
          .eq('user_id', session.user.id)
          .eq('vdp_url', negotiationVdpUrl)
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          logger.warn('[NegotiationChat] feed lookup failed:', error);
          return;
        }
        if (data?.id) setFeedEntryIdForListing(data.id);
      } catch (err) {
        if (!cancelled) {
          logger.warn('[NegotiationChat] feed lookup threw:', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id, negotiationVdpUrl]);

  // Card-click → DooCars listing detail. Falls back to the dealer's VDP only
  // when we can't find a matching internal feed row.
  const handleOpenListing = useCallback(() => {
    if (feedEntryIdForListing) {
      router.push(`/listing/${feedEntryIdForListing}`);
      return;
    }
    handleOpenVdp();
  }, [feedEntryIdForListing, router, handleOpenVdp]);

  // ---------- PreferencesSummary edit handler ----------
  // Opens PreferenceEditor modal. On save, persists to negotiations.setup_config
  // via supabase and dispatches a draft regenerate so the new preferences are
  // reflected in the next AI draft.
  const [isPrefEditorOpen, setIsPrefEditorOpen] = useState(false);

  const handleEditPreferences = useCallback(() => {
    setIsPrefEditorOpen(true);
  }, []);

  const handleClosePrefEditor = useCallback(() => {
    setIsPrefEditorOpen(false);
  }, []);

  const handleSavePreferences = useCallback(
    async ({ budget, priorities }) => {
      if (!negotiation?.id) return;
      const supabaseClient = (await import('../utils/supabaseClient')).default;
      // Merge into existing setup_config so we don't clobber other fields the
      // setup screen wrote (tone, urgency, autonomy preferences, etc.).
      // Use the same key names the setup flow uses: priorities_ranked.
      // Strip any legacy `budget` / `priorities` keys to keep one source of
      // truth (budget lives at row level as buyer_reservation_price).
      const existingConfig = negotiation?.setup_config || {};
      const { budget: _legacyBudget, priorities: _legacyPriorities, ...rest } =
        existingConfig;
      const nextConfig = {
        ...rest,
        priorities_ranked: priorities,
      };
      const { error: updateErr } = await supabaseClient
        .from('negotiations')
        .update({
          setup_config: nextConfig,
          buyer_reservation_price: budget,
        })
        .eq('id', negotiation.id);
      if (updateErr) {
        logger.error(
          '[NegotiationChat] preferences setup_config update failed:',
          updateErr
        );
        throw updateErr;
      }
      // Trigger a draft regenerate so the next draft uses the new prefs.
      try {
        await apiService.requestNegotiationAction('regenerate_draft', {
          negotiation_id: negotiation.id,
        });
      } catch (err) {
        logger.warn(
          '[NegotiationChat] regenerate after preference update failed:',
          err
        );
      }
    },
    [negotiation?.id, negotiation?.setup_config]
  );

  // ---------- Filtered chat thread ----------
  // Drafts (role 'agent_draft') are rendered inside the action surface, not
  // inside the thread itself. Discarded rows are also hidden from the thread.
  const threadMessages = useMemo(() => {
    if (!Array.isArray(messages)) return [];
    return messages.filter(
      (m) =>
        m.role !== 'agent_draft' &&
        !(m.role === 'user_written' && m.status === 'safety_blocked') &&
        m.status !== 'discarded'
    );
  }, [messages]);

  // Locate the most recent approved coaching-script draft so the
  // consolidated FormBridgeCard can switch into its post-send "waiting for
  // dealer" state (and still let the user copy the script they sent). Without
  // this the card would not know an approved script existed, since
  // selectActiveDrafts filters approved drafts out of the active list.
  const approvedScript = useMemo(() => {
    if (!Array.isArray(messages)) return null;
    const approvedDrafts = messages
      .filter((m) => m.role === 'agent_draft' && m.status === 'approved')
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    for (const d of approvedDrafts) {
      const raw = d.content;
      if (typeof raw !== 'string' || !raw.startsWith('{')) continue;
      try {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.coachingText) {
          return {
            id: d.id,
            text: String(parsed.coachingText)
              .replace(/^[""]|[""]$/g, '')
              .trim(),
            talkingPoints: parsed.talkingPoints
              ? String(parsed.talkingPoints)
                  .replace(/\*\*(.+?)\*\*/g, '$1')
                  .replace(/^\*\s+/gm, '\u2022 ')
                  .replace(/^-\s+/gm, '\u2022 ')
              : null,
          };
        }
      } catch {}
    }
    return null;
  }, [messages]);

  // ---------- Auto-scroll to latest message ----------
  // MainColumn is the scroll container now (the chat thread flows inside it).
  // Pin the viewport to the latest message on first paint and whenever a new
  // message arrives. `animated:false` on the first run avoids a visible scroll
  // animation when opening the screen.
  const pageScrollRef = useRef(null);
  const hasInitialScrollRef = useRef(false);
  useEffect(() => {
    if (!pageScrollRef.current || threadMessages.length === 0) return;
    const animated = hasInitialScrollRef.current;
    requestAnimationFrame(() => {
      pageScrollRef.current?.scrollToEnd?.({ animated });
    });
    hasInitialScrollRef.current = true;
  }, [threadMessages.length]);

  const dealerName =
    negotiation?.listing_snapshot_at_start?.dealer?.name ||
    negotiation?.listing_snapshot_at_start?.dealer_name ||
    '';

  // ---------- Loading state ----------
  // Use Skeleton placeholders for the rails and a chat skeleton for the thread
  // until the realtime hook returns a negotiation row.
  const showLoading = !negotiation && (realtimeLoading || eventsLoading);

  // ---------- Action-surface render ----------
  const renderActionSurface = () => {
    if (surface === 'closed' || surface === 'paused' || surface === 'idle') {
      return null;
    }

    if (surface === 'handoff') {
      return <HandoffCard negotiation={negotiation} />;
    }

    if (surface === 'needs_input') {
      // Mobile and desktop both render NeedsInputPrompt inside the central
      // column area. On mobile the MobileDraftSheet would otherwise wrap
      // DraftBlock, but for needs_input the prompt is rendered inline.
      return (
        <NeedsInputPrompt
          onSubmit={handleNeedsInputSubmit}
        />
      );
    }

    // 'guardrail' | 'generating' | 'draft' — desktop renders DraftBlock,
    // mobile renders MobileDraftSheet (handled outside this function so it
    // can be a sibling to the chat scroll container).
    if (isMobile) {
      // The MobileDraftSheet itself is rendered at the screen-root level
      // (sibling to the chat scroll container) so it can use position
      // absolute against the screen-root. Returning null here avoids
      // double-rendering.
      return null;
    }

    return (
      <DraftBlock
        activeDrafts={activeDrafts}
        selectedVersionId={selectedDraftId}
        onSelectVersion={setSelectedDraftId}
        onApprove={handleApprove}
        onEdit={handleEdit}
        onSaveEdit={handleInlineSaveEdit}
        onRegenerate={handleRegenerate}
        onDiscard={handleDiscard}
        onReport={handleReport}
        onEnd={handleEndNegotiationFromMenu}
        regenCount={regenCount}
        regenMax={regenMax}
        negotiation={negotiation}
        surface={surface}
      />
    );
  };

  // ---------- Center column ----------
  const renderCenter = () => (
    <CenterCol>
      {banners.length > 0 ? (
        <BannerStackWrap>
          {banners.map((banner) =>
            renderBanner({
              banner,
              negotiation,
              formBridgeDismissed,
              approvedScriptText: approvedScript?.text || null,
              onFormBridgeDismiss: handleFormBridgeDismiss,
              onSendFollowUp: handleSendFollowUp,
              onTryCalling: handleTryCalling,
              onGuidedSubmit: handleGuidedSubmit,
              onFindSimilarListing: handleFindSimilarListing,
              onEndNegotiation: handleEndNegotiationFromMenu,
            })
          )}
        </BannerStackWrap>
      ) : null}

      <ChatScroll>
        <ChatInner>
          {showLoading ? (
            <Skeleton variant="chat" count={4} ariaLabel="Loading messages" />
          ) : threadMessages.length === 0 &&
            (surface === 'generating' || surface === 'idle') ? (
            // surface='idle' with zero thread messages happens in the gap
            // between negotiation creation and the worker inserting the first
            // draft row (status='generating'). Both states map to "opening
            // draft is being composed" so the user sees feedback immediately.
            <CenterMessage>
              <ActivityIndicator
                size="small"
                color={REDESIGN_COLORS.ctaBlue}
                style={{ marginBottom: 12 }}
              />
              <CenterText>Composing your opening draft...</CenterText>
            </CenterMessage>
          ) : threadMessages.length === 0 ? null : (
            threadMessages.map((m) => (
              <View key={m.id}>
                <ChatBubble
                  message={m}
                  dealerName={dealerName}
                  onWrapUpDeal={handleWrapUpDeal}
                  onKeepNegotiating={handleKeepNegotiating}
                />
              </View>
            ))
          )}
          {waitingForDraft && !showLoading ? (
            <DraftPendingRow>
              <TypingIndicator />
              <DraftPendingText>Composing your reply...</DraftPendingText>
            </DraftPendingRow>
          ) : null}
        </ChatInner>
      </ChatScroll>

      {/* Action surface (DraftBlock / Handoff / NeedsInput / nothing). */}
      {(() => {
        const node = renderActionSurface();
        if (!node) return null;
        return <ActionWrap>{node}</ActionWrap>;
      })()}

      {/* Dealer-spoke entry point. Renders at the bottom of the center column
          as a collapsible bar when the user has acted in guided mode but no
          new draft is active (surface === 'idle'). Replaces the old top-of-
          screen GuidedInput banner. */}
      {banners.some((b) => b.kind === 'post_call_coaching') &&
      surface === 'idle' ? (
        <DealerMessageBar
          negotiationId={negotiation?.id}
          onSubmit={handleGuidedSubmit}
        />
      ) : null}
    </CenterCol>
  );

  // ---------- Layout dispatch ----------
  return (
    <ScreenRoot>
      {isMobile ? (
        <ScreenHeader title="Negotiation" />
      ) : (
        <DesktopHeader
          logo={LogoWhite}
          navLinks={navLinks}
          variant="dark"
          onLogoPress={() => router.push('/home')}
        />
      )}

      <MainColumn $isMobile={isMobile} ref={pageScrollRef}>
        {!isMobile ? (
          <BackRow
            onPress={handleBack}
            accessibilityRole="button"
            accessibilityLabel="Back to negotiations"
          >
            <MaterialCommunityIcons
              name="chevron-left"
              size={14}
              color="rgba(255,255,255,0.55)"
            />
            <BackText>Back to negotiations</BackText>
          </BackRow>
        ) : null}

        {showLoading ? (
          <Skeleton variant="card" count={1} ariaLabel="Loading vehicle" />
        ) : (
          <ListingHeaderCard
            negotiation={negotiation}
            currentRound={negotiation?.current_round || 0}
            isMobile={isMobile}
            onBack={isMobile ? handleBack : undefined}
            onOpenListing={handleOpenListing}
            onOpenVdp={handleOpenVdp}
            onEditPreferences={handleEditPreferences}
            onEndNegotiation={handleEndNegotiationFromMenu}
          />
        )}

        {renderCenter()}
      </MainColumn>

      {/* Mobile bottom sheet — sibling to MainColumn so position:absolute
          anchors to ScreenRoot, not the chat scroll container. */}
      {isMobile &&
      ['draft', 'guardrail', 'generating'].includes(surface) ? (
        <MobileDraftSheet
          activeDrafts={activeDrafts}
          selectedVersionId={selectedDraftId}
          onSelectVersion={setSelectedDraftId}
          onApprove={handleApprove}
          onEdit={handleEdit}
          onSaveEdit={handleInlineSaveEdit}
          onRegenerate={handleRegenerate}
          onDiscard={handleDiscard}
          onReport={handleReport}
          onEnd={handleEndNegotiationFromMenu}
          regenCount={regenCount}
          regenMax={regenMax}
          negotiation={negotiation}
          surface={surface}
          expanded={sheetExpanded}
          onExpand={expandSheet}
          onCollapse={collapseSheet}
        />
      ) : null}

      {/* Report modal — controlled by local state. */}
      <ReportDraftModal
        visible={!!reportingDraftId}
        messageId={reportingDraftId}
        onClose={() => setReportingDraftId(null)}
      />

      {/* End-negotiation confirmation dialog. */}
      <EndNegotiationDialog
        visible={isEndDialogOpen}
        isAssisted={negotiation?.autonomy === 'assisted'}
        onClose={() => setIsEndDialogOpen(false)}
        onConfirm={handleEndNegotiationConfirm}
      />

      {/* Preference editor modal — wired to PreferencesSummaryCard.onEdit. */}
      <PreferenceEditor
        visible={isPrefEditorOpen}
        negotiation={negotiation}
        onClose={handleClosePrefEditor}
        onSave={handleSavePreferences}
      />

      {/* Edit-draft modal — opens when user picks Edit from the overflow ▾. */}
      <EditDraftModal
        visible={!!editingDraft}
        subject={editingDraft?.subject ?? null}
        content={editingDraft?.content ?? ''}
        onSave={handleSaveEdit}
        onClose={handleCloseEdit}
      />
    </ScreenRoot>
  );
};

export default NegotiationChatScreen;
